import React, { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import PortalShell from "./PortalShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Package, Receipt, FileText, MessageSquare, Upload, TrendingUp, DollarSign, Truck } from "lucide-react";

const BYTES_PER_KB = 1024;

export default function CustomerPortal() {
  return (
    <PortalShell kind="customer">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="documents" element={<Documents />} />
        <Route path="messages" element={<Messages />} />
      </Routes>
    </PortalShell>
  );
}

function Overview() {
  const [shipments, setShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    api.get("/shipments/mine").then(r => setShipments(r.data)).catch(() => {});
    api.get("/invoices/mine").then(r => setInvoices(r.data)).catch(() => {});
    api.get("/quotes/mine").then(r => setQuotes(r.data)).catch(() => {});
  }, []);
  const activeCount = shipments.filter(s => s.status !== "Delivered").length;
  const unpaid = invoices.filter(i => i.status !== "Paid").reduce((s, i) => s + i.amount, 0);
  return (
    <div className="space-y-6" data-testid="customer-overview">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat icon={Truck} label="Active shipments" value={activeCount} />
        <Stat icon={Package} label="Total shipments" value={shipments.length} />
        <Stat icon={DollarSign} label="Outstanding" value={`$${unpaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
        <Stat icon={FileText} label="Recent quotes" value={quotes.length} />
      </div>
      <Panel title="Active shipments" cta={<Link to="shipments" className="text-sm text-[#2E7BC7] font-semibold">View all</Link>}>
        {shipments.slice(0, 5).map(s => <ShipmentRow key={s.shipment_id} s={s} />)}
        {!shipments.length && <Empty>Your shipments will appear here.</Empty>}
      </Panel>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="h-5 w-5 text-[#2E7BC7]" />
      <div className="mt-3 font-label text-xs text-slate-500">{label}</div>
      <div className="mt-1 font-display font-black text-2xl text-[#0B3D6E]">{value}</div>
    </div>
  );
}
function Panel({ title, cta, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div className="font-display font-bold text-[#0B3D6E]">{title}</div>
        {cta}
      </div>
      <div>{children}</div>
    </div>
  );
}
const Empty = ({ children }) => <div className="p-8 text-center text-sm text-slate-500">{children}</div>;

function ShipmentRow({ s }) {
  const color = { "In Transit": "bg-blue-100 text-blue-800", "Delivered": "bg-green-100 text-green-800", "Booked": "bg-orange-100 text-orange-800" }[s.status] || "bg-gray-100 text-gray-800";
  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50">
      <div>
        <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{s.shipment_id}</div>
        <div className="text-xs text-slate-500 mt-1">{s.origin} → {s.destination} · {s.equipment_type}</div>
      </div>
      <span className={`pill ${color}`}>{s.status}</span>
    </div>
  );
}

function Quotes() {
  const [q, setQ] = useState([]);
  useEffect(() => { api.get("/quotes/mine").then(r => setQ(r.data)).catch(() => {}); }, []);
  return (
    <Panel title="Quote history" cta={<Link to="/quote" className="btn-primary-apex text-sm">New quote</Link>}>
      {q.map(x => (
        <div key={x.quote_id} className="p-4 border-b border-gray-100 flex items-center justify-between" data-testid={`quote-row-${x.quote_id}`}>
          <div>
            <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{x.quote_id}</div>
            <div className="text-xs text-slate-500">{x.pickup_city} → {x.delivery_city} · {x.equipment_type}</div>
          </div>
          <div className="text-right">
            <span className={`pill ${x.status === "Quoted" ? "bg-blue-100 text-blue-800" : x.status === "Accepted" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}>{x.status}</span>
            <div className="text-xs text-slate-500 mt-1">{x.status === "Pending Review" ? "Ops will follow up" : ""}</div>
          </div>
        </div>
      ))}
      {!q.length && <Empty>Submit a quote to see it here.</Empty>}
    </Panel>
  );
}
function Shipments() {
  const [s, setS] = useState([]);
  useEffect(() => { api.get("/shipments/mine").then(r => setS(r.data)).catch(() => {}); }, []);
  return <Panel title="All shipments">{s.map(x => <ShipmentRow key={x.shipment_id} s={x} />)}{!s.length && <Empty>No shipments yet.</Empty>}</Panel>;
}
function Invoices() {
  const [inv, setInv] = useState([]);
  const load = () => api.get("/invoices/mine").then(r => setInv(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const pay = async (id) => {
    try { await api.post("/invoices/pay", { invoice_id: id }); toast.success("Invoice marked paid"); load(); }
    catch { toast.error("Payment failed"); }
  };
  return (
    <Panel title="Invoices">
      {inv.map(i => (
        <div key={i.invoice_id} className="p-4 border-b border-gray-100 flex items-center justify-between" data-testid={`invoice-row-${i.invoice_id}`}>
          <div>
            <div className="font-mono text-sm font-semibold text-[#0B3D6E]">{i.invoice_id}</div>
            <div className="text-xs text-slate-500">Due {i.due_date}</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-display font-bold text-[#0B3D6E]">${i.amount.toFixed(2)}</div>
            {i.status === "Paid" ? (
              <span className="pill bg-green-100 text-green-800">Paid</span>
            ) : (
              <button className="btn-primary-apex text-xs" onClick={() => pay(i.invoice_id)} data-testid={`pay-${i.invoice_id}`}>Pay now</button>
            )}
          </div>
        </div>
      ))}
      {!inv.length && <Empty>No invoices.</Empty>}
    </Panel>
  );
}
function Documents() {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("shipping_document");
  const load = () => api.get("/documents/mine").then(r => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("document_type", type);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch { toast.error("Upload failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="font-display font-bold text-[#0B3D6E] mb-3">Upload document</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={type} onChange={(e) => setType(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="doc-type-select">
            <option value="shipping_document">Shipping document</option>
            <option value="BOL">Bill of Lading</option>
            <option value="POD">Proof of Delivery</option>
            <option value="insurance">Insurance certificate</option>
          </select>
          <label className="btn-primary-apex text-sm cursor-pointer inline-flex items-center gap-2">
            <Upload className="h-4 w-4" /> Choose file
            <input type="file" className="hidden" onChange={onFile} data-testid="doc-upload-input" />
          </label>
        </div>
      </div>
      <Panel title="My documents">
        {docs.map(d => (
          <div key={d.doc_id} className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div><div className="font-semibold text-sm text-[#0B3D6E]">{d.filename}</div><div className="text-xs text-slate-500">{d.document_type} · {(d.size_bytes / BYTES_PER_KB).toFixed(1)} KB</div></div>
            <div className="text-xs text-slate-500">{new Date(d.uploaded_at).toLocaleDateString()}</div>
          </div>
        ))}
        {!docs.length && <Empty>Upload BOLs, PODs, or shipping paperwork.</Empty>}
      </Panel>
    </div>
  );
}
function Messages() {
  const [msgs, setMsgs] = useState([]);
  const [f, setF] = useState({ subject: "", body: "" });
  const load = () => api.get("/messages/mine").then(r => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/messages", f); setF({ subject: "", body: "" }); toast.success("Message sent"); load(); }
    catch { toast.error("Send failed"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={send} className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-testid="message-form">
        <div className="font-display font-bold text-[#0B3D6E]">Message operations</div>
        <input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] outline-none" data-testid="msg-subject" />
        <textarea required rows={5} placeholder="How can we help?" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] outline-none" data-testid="msg-body" />
        <button className="btn-primary-apex" data-testid="msg-send-btn">Send</button>
      </form>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {msgs.map(m => (
          <div key={m.msg_id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm text-[#0B3D6E]">{m.subject}</div>
              <span className={`pill ${m.from_ops ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>{m.from_ops ? "Ops" : "You"}</span>
            </div>
            <p className="text-sm text-slate-600 mt-2">{m.body}</p>
            <div className="text-xs text-slate-400 mt-2">{new Date(m.created_at).toLocaleString()}</div>
          </div>
        ))}
        {!msgs.length && <Empty>No messages yet.</Empty>}
      </div>
    </div>
  );
}
